<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import KanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const contactAttributeDefs = useMapGetter('attributes/getContactAttributes');

// Returns attribute defs for a specific funnel (card.attribute_key)
const getCardAttributeDefs = attributeKey =>
  (contactAttributeDefs.value || []).filter(
    attr =>
      attr.attributeDisplayType !== 'crm' &&
      attr.attributeKey !== 'kbw_card_color' &&
      (!attr.funnelKeys?.length || attr.funnelKeys.includes(attributeKey))
  );

// Kanban cards state
const kanbanCards = ref([]);
const listAttributes = ref([]);
const loadingCards = ref(false);
const editingCardId = ref(null);
const editingCardAttrs = ref({});
const savingCard = ref(false);

const fetchContactCards = async () => {
  if (!props.selectedContact?.id) return;
  loadingCards.value = true;
  try {
    const res = await KanbanAPI.get({ contact_id: props.selectedContact.id });
    kanbanCards.value = res.data?.cards || [];
    listAttributes.value = res.data?.listAttributes || [];
  } catch {
    kanbanCards.value = [];
  } finally {
    loadingCards.value = false;
  }
};

const getPipelineName = attributeKey => {
  const attr = listAttributes.value.find(a => a.attribute_key === attributeKey);
  return attr?.attribute_display_name || attributeKey;
};

const formatDateDisplay = value => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
};

const formatDateForInput = value => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const formatCurrency = value => {
  if (value === '' || value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatDisplayValue = (value, type) => {
  if (value === '' || value === null || value === undefined) return '—';
  if (type === 'date') return formatDateDisplay(value);
  if (type === 'currency') return formatCurrency(value);
  if (type === 'number') return Number(value).toLocaleString('pt-BR');
  return value;
};

const getCardAttrEntries = card => {
  const attrs = card.additional_attributes || {};
  return getCardAttributeDefs(card.attribute_key)
    .filter(def => {
      const v = attrs[def.attributeKey];
      return v !== undefined && v !== null && v !== '';
    })
    .map(def => ({
      key: def.attributeKey,
      label: def.attributeDisplayName,
      value: attrs[def.attributeKey],
      type: def.attributeDisplayType,
    }));
};

const startEditingCard = card => {
  const cardAttrs = card.additional_attributes || {};
  editingCardAttrs.value = getCardAttributeDefs(card.attribute_key).reduce(
    (acc, def) => {
      const val = cardAttrs[def.attributeKey];
      acc[def.attributeKey] =
        def.attributeDisplayType === 'date'
          ? formatDateForInput(val)
          : (val ?? '');
      return acc;
    },
    {}
  );
  editingCardId.value = card.id;
};

const cancelEditingCard = () => {
  editingCardId.value = null;
  editingCardAttrs.value = {};
};

const saveCardAttrs = async card => {
  savingCard.value = true;
  try {
    const editedAttrs = Object.fromEntries(
      Object.entries(editingCardAttrs.value).map(([k, v]) => [
        k,
        v === '' ? null : v,
      ])
    );
    const additionalAttributes = {
      ...(card.additional_attributes || {}),
      ...editedAttrs,
    };
    await KanbanAPI.update(card.id, {
      additional_attributes: additionalAttributes,
    });
    await fetchContactCards();
    editingCardId.value = null;
    editingCardAttrs.value = {};
  } finally {
    savingCard.value = false;
  }
};

watch(
  () => props.selectedContact?.id,
  id => {
    if (id) fetchContactCards();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4 px-4 py-4">
    <!-- Header -->
    <div class="flex items-center gap-2">
      <span class="i-lucide-kanban size-3.5 text-n-brand" />
      <span class="text-xs font-semibold text-n-brand uppercase tracking-wider">
        {{ t('KANBAN.CRM_FUNNELS') }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loadingCards" class="flex justify-center py-6">
      <span class="i-lucide-loader-2 animate-spin text-n-slate-10 size-5" />
    </div>

    <!-- Cards -->
    <div v-else-if="kanbanCards.length" class="flex flex-col gap-3">
      <div
        v-for="card in kanbanCards"
        :key="card.id"
        class="rounded-xl border overflow-hidden"
        :style="{ borderColor: (card.color || '#3b82f6') + '50' }"
      >
        <!-- Card header -->
        <div
          class="flex items-center gap-2 px-3 py-2"
          :style="{ backgroundColor: (card.color || '#3b82f6') + '12' }"
        >
          <div
            class="w-2 h-2 rounded-full shrink-0"
            :style="{ backgroundColor: card.color || '#3b82f6' }"
          />
          <span class="text-xs font-semibold text-n-slate-12 truncate flex-1">
            {{ getPipelineName(card.attribute_key) }}
          </span>
          <span
            v-if="card.attribute_value"
            class="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
            :style="{
              backgroundColor: (card.color || '#3b82f6') + '25',
              color: card.color || '#3b82f6',
            }"
          >
            {{ card.attribute_value }}
          </span>
          <div
            v-if="card.conversation"
            class="flex items-center gap-1 shrink-0"
          >
            <span class="i-lucide-message-square text-n-slate-10 size-3" />
            <span class="text-xs text-n-slate-10">{{
              `#${card.conversation.display_id}`
            }}</span>
          </div>
        </div>

        <!-- EDIT MODE -->
        <div
          v-if="editingCardId === card.id"
          class="px-3 py-3 bg-white dark:bg-n-solid-1"
        >
          <div class="grid grid-cols-2 gap-2 mb-3">
            <div
              v-for="def in getCardAttributeDefs(card.attribute_key)"
              :key="def.attributeKey"
              class="flex flex-col gap-1"
              :class="
                def.attributeKey === 'observacoes' ||
                def.attributeDisplayType === 'text'
                  ? 'col-span-2'
                  : ''
              "
            >
              <label class="text-xs text-n-slate-10">{{
                def.attributeDisplayName
              }}</label>

              <select
                v-if="def.attributeDisplayType === 'list'"
                v-model="editingCardAttrs[def.attributeKey]"
                class="w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option value="">
                  {{ t('KANBAN.CONTACT_MODAL.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="opt in def.attributeValues"
                  :key="opt"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>

              <input
                v-else-if="def.attributeDisplayType === 'date'"
                v-model="editingCardAttrs[def.attributeKey]"
                type="date"
                class="w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand"
              />

              <input
                v-else-if="
                  def.attributeDisplayType === 'currency' ||
                  def.attributeDisplayType === 'number'
                "
                v-model="editingCardAttrs[def.attributeKey]"
                type="number"
                step="0.01"
                min="0"
                class="w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand"
              />

              <textarea
                v-else-if="def.attributeKey === 'observacoes'"
                v-model="editingCardAttrs[def.attributeKey]"
                rows="2"
                class="w-full px-2 py-1.5 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand resize-none"
              />

              <input
                v-else
                v-model="editingCardAttrs[def.attributeKey]"
                type="text"
                class="w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>
          </div>

          <div class="flex items-center justify-end gap-2">
            <button
              class="px-3 py-1.5 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
              :disabled="savingCard"
              @click="cancelEditingCard"
            >
              {{ t('KANBAN.CONTACT_MODAL.CANCEL_BUTTON') }}
            </button>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50"
              :style="{ backgroundColor: card.color || '#3b82f6' }"
              :disabled="savingCard"
              @click="saveCardAttrs(card)"
            >
              <span
                v-if="savingCard"
                class="i-lucide-loader-2 animate-spin size-3"
              />
              <span v-else class="i-lucide-check size-3" />
              {{ t('KANBAN.CONTACT_MODAL.SAVE_BUTTON') }}
            </button>
          </div>
        </div>

        <!-- VIEW MODE -->
        <div v-else class="px-3 py-3 bg-white dark:bg-n-solid-1">
          <div
            v-if="getCardAttrEntries(card).length"
            class="grid grid-cols-2 gap-x-3 gap-y-2 mb-2"
          >
            <div
              v-for="attr in getCardAttrEntries(card)"
              :key="attr.key"
              :class="attr.key === 'observacoes' ? 'col-span-2' : ''"
            >
              <p class="text-xs text-n-slate-10">{{ attr.label }}</p>
              <p class="text-sm font-medium text-n-slate-12 break-words">
                {{ formatDisplayValue(attr.value, attr.type) }}
              </p>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10 mb-2">
            {{ t('KANBAN.CONTACT_MODAL.NO_ATTRIBUTES') }}
          </p>
          <button
            v-if="getCardAttributeDefs(card.attribute_key).length"
            class="text-xs font-medium transition-colors hover:opacity-80"
            :style="{ color: card.color || '#3b82f6' }"
            @click="startEditingCard(card)"
          >
            {{ t('KANBAN.CONTACT_MODAL.EDIT_BUTTON') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="flex flex-col items-center justify-center py-8 gap-2">
      <span class="i-lucide-kanban text-n-slate-8 size-8" />
      <p class="text-sm text-n-slate-10 text-center">
        {{ t('KANBAN.CONTACT_MODAL.NO_ATTRIBUTES') }}
      </p>
    </div>
  </div>
</template>
